// Close-position dialog for an individual opening trade. Mirrors the legacy
// showCloseDialog + execClose flow: smart (3× maker retry shift), user-priced
// limit, or market (taker). Posts to /grids/:gridId/close-trade/:tradeId.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "@/lib/api-client";
import Modal from "@/components/common/modal";
import { fmt } from "@/lib/format";
import type { Trade } from "@/types/api";

interface Props {
  gridId: string;
  trade: Trade;
  symbol: string;
  livePrice: number | null;
  onClose: () => void;
}

type Mode = "shift" | "limit" | "market";

export default function CloseTradeModal({
  gridId,
  trade,
  symbol,
  livePrice,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const exitSide = trade.side === "buy" ? "SELL" : "BUY";
  const [customPrice, setCustomPrice] = useState(
    String(livePrice ?? trade.price)
  );
  const [error, setError] = useState("");

  const mutation = useMutation({
    mutationFn: async (mode: Mode) => {
      let path = `/grids/${gridId}/close-trade/${trade.id}?mode=${mode}`;
      // Only limit mode uses a user-specified price; smart/market price server-side.
      if (mode === "limit" && customPrice) {
        path += `&price=${encodeURIComponent(customPrice)}`;
      }
      return apiPost<{ message?: string; pnl?: number; error?: string }>(path);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grid-detail", gridId] });
      qc.invalidateQueries({ queryKey: ["grid-pnl", gridId] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal open onClose={onClose} title="Close Position" maxWidth="360px">
      <div className="mb-3 text-dim text-sm">
        <div>
          {trade.side.toUpperCase()} {symbol} @ {fmt(trade.price, symbol)}
        </div>
        <div className="text-muted text-xs">
          Qty: {trade.quantity}
          {livePrice ? ` · Now: ${fmt(livePrice, symbol)}` : ""}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <button
          className="w-full text-sm py-2 rounded bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 transition-colors disabled:opacity-50"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate("shift")}
        >
          Smart {exitSide} (3× maker retry)
        </button>

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-[0.7rem] uppercase tracking-wider text-muted mb-1">
              Price
            </label>
            <input
              type="number"
              step="any"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
              className="w-full bg-bg border border-border rounded px-2.5 py-1.5 text-sm text-text outline-none focus:border-accent/60 transition-colors"
            />
          </div>
          <button
            className="text-sm px-3 py-1.5 rounded border border-border text-dim hover:text-text whitespace-nowrap transition-colors disabled:opacity-50"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate("limit")}
          >
            {exitSide} Limit
          </button>
        </div>

        <button
          className="w-full text-sm py-2 rounded border border-danger/40 text-danger hover:bg-danger/10 transition-colors disabled:opacity-50"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate("market")}
        >
          {exitSide} Market (taker)
        </button>

        <button
          className="w-full text-sm py-2 rounded border border-border text-muted hover:text-text transition-colors"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>

      {error && <div className="text-danger text-xs mt-2">{error}</div>}
    </Modal>
  );
}
